import React, { FC, useRef } from 'react';
import StyledComponent from '../../core/StyledComponent';
import { px2rem } from '../../core';
import copy from 'copy-to-clipboard';
import { DesignSettings } from '@mnz/builder';
import Modal, { ModalProps } from '../components/modal/Modal';
import { useTheme } from '@mui/material';
import SocialIcon from '../components/icon/SocialIcon';
import { Close, Link } from '@menuzen/icon/build/outline';

interface ShareModalProps extends ModalProps {
    designSettings: DesignSettings;
}

const ShareModal: FC<ShareModalProps> = ({ designSettings, ...props }) => {
    const inputRef = useRef<HTMLInputElement>();
    const theme = useTheme();
    const handleCopy = (url: string) => {
        copy(url);
        inputRef.current.select();
    };
    return (
        <Modal
            {...props}
            xs={{
                padding: px2rem(20),
                borderRadius: `${px2rem(10)}`,
                backgroundColor: 'white',
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.primary.main,
            }}
            sm={{
                minWidth: 450,
            }}
        >
            <StyledComponent
                xs={{
                    display: 'flex',
                    justifyContent: 'end',
                }}
            >
                <StyledComponent
                    xs={{
                        width: px2rem(20),
                        height: px2rem(20),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: px2rem(20),
                        cursor: 'pointer',
                        color: theme.palette.primary70,
                    }}
                    onClick={props.onClose}
                >
                    <Close />
                </StyledComponent>
            </StyledComponent>
            <StyledComponent
                xs={{
                    fontSize: px2rem(20),
                    fontWeight: 700,
                    marginTop: px2rem(4),
                }}
            >
                Share
            </StyledComponent>
            <StyledComponent
                xs={{
                    marginTop: px2rem(16),
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: px2rem(22),
                    marginLeft: px2rem(-8),
                    marginRight: px2rem(-8),
                }}
            >
                <StyledComponent
                    xs={{
                        width: px2rem(50),
                        height: px2rem(50),
                        borderRadius: '50%',
                        fontSize: px2rem(30),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: `0 ${px2rem(8)}`,
                        cursor: 'pointer',
                        background: 'linear-gradient(85.83deg, #FBF7F6 1.91%, #F4FCFC 99.17%)',
                    }}
                    as={'a'}
                    className={'core_emailclick'}
                    href={`mailto:?to=&body=${encodeURIComponent(designSettings.url)}`}
                    target={`_blank`}
                >
                    <SocialIcon social={'MAIL'} />
                </StyledComponent>
                <StyledComponent
                    xs={{
                        width: px2rem(50),
                        height: px2rem(50),
                        borderRadius: '50%',
                        fontSize: px2rem(30),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: `0 ${px2rem(8)}`,
                        cursor: 'pointer',
                        background: 'linear-gradient(85.83deg, #FBF7F6 1.91%, #F4FCFC 99.17%)',
                    }}
                    as={'a'}
                    className={'core_share_facebook'}
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(designSettings.url)}`}
                    target={`_blank`}
                >
                    <SocialIcon social={'FACEBOOK'} />
                </StyledComponent>
                <StyledComponent
                    xs={{
                        width: px2rem(50),
                        height: px2rem(50),
                        borderRadius: '50%',
                        fontSize: px2rem(30),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: `0 ${px2rem(8)}`,
                        cursor: 'pointer',
                        background: 'linear-gradient(85.83deg, #FBF7F6 1.91%, #F4FCFC 99.17%)',
                    }}
                    as={'a'}
                    className={'core_share_twitter'}
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(designSettings.url)}`}
                    target={`_blank`}
                >
                    <SocialIcon social={'TWITTER'} />
                </StyledComponent>
                <StyledComponent
                    xs={{
                        width: px2rem(50),
                        height: px2rem(50),
                        borderRadius: '50%',
                        fontSize: px2rem(30),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: `0 ${px2rem(8)}`,
                        cursor: 'pointer',
                        background: 'linear-gradient(85.83deg, #FBF7F6 1.91%, #F4FCFC 99.17%)',
                    }}
                    as={'a'}
                    className={'core_share_whatsapp'}
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(designSettings.url)}`}
                    target={`_blank`}
                >
                    <SocialIcon social={'WHATSAPP'} />
                </StyledComponent>
                <StyledComponent
                    xs={{
                        width: px2rem(50),
                        height: px2rem(50),
                        borderRadius: '50%',
                        fontSize: px2rem(30),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: `0 ${px2rem(8)}`,
                        cursor: 'pointer',
                        background: 'linear-gradient(85.83deg, #FBF7F6 1.91%, #F4FCFC 99.17%)',
                    }}
                    as={'a'}
                    className={'core_share_messenger'}
                    href={`https://www.facebook.com/dialog/send?app_id=4210561472354035&link=${encodeURIComponent(
                        designSettings.url,
                    )}&redirect_uri=${designSettings.url}`}
                    target={`_blank`}
                >
                    <SocialIcon social={'FACEBOOK_MESSENGER'} />
                </StyledComponent>
            </StyledComponent>
            <StyledComponent
                xs={{
                    border: `1px solid ${theme.palette.primary40}`,
                    borderRadius: `99999px`,
                    background: theme.palette.primary10,
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: px2rem(20),
                    marginBottom: px2rem(20),
                    color: theme.palette.primary80,
                }}
            >
                <StyledComponent
                    as={'span'}
                    xs={{ fontSize: px2rem(12), paddingLeft: px2rem(10), paddingRight: px2rem(6) }}
                >
                    <Link />
                </StyledComponent>
                <StyledComponent
                    ref={inputRef}
                    as={'input'}
                    defaultValue={designSettings.url}
                    readonly={true}
                    size={1}
                    xs={{ fontSize: px2rem(13), flexGrow: 1, background: 'transparent' }}
                />
                <StyledComponent
                    as={'button'}
                    xs={{
                        fontSize: px2rem(13),
                        fontWeight: 600,
                        color: 'white',
                        background: theme.palette.primary.main,
                        padding: `${px2rem(10)} ${px2rem(14)}`,
                        borderTopRightRadius: `99999px`,
                        borderBottomRightRadius: `99999px`,
                    }}
                    className={'core_share_link'}
                    onClick={() => handleCopy(designSettings.url)}
                >
                    Copy Link
                </StyledComponent>
            </StyledComponent>
        </Modal>
    );
};

export default ShareModal;
